import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  createPermissionRequest,
  approvePermissionRequest,
  denyPermissionRequest,
  cancelPermissionRequest,
  type PermissionRequest,
  type PermissionRequestCreate,
  type PermissionRequestApprove,
  type PermissionRequestDeny,
} from '@/api/permissionRequests';
import {
  createPermissionRequestBatch,
  type PermissionRequestBatch,
  type PermissionRequestBatchResponse,
} from '@/api/userSettings';
import { queryKeys } from '@/lib/queryKeys';

function invalidatePermissionRequestsCache(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.permissionRequests.all });
}

export function useCreatePermissionRequestBatchMutation(
  options?: UseMutationOptions<PermissionRequestBatchResponse, Error, PermissionRequestBatch>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: (batchRequest) => createPermissionRequestBatch(batchRequest),
    onSuccess: async (data, variables, onMutateResult, context) => {
      invalidatePermissionRequestsCache(queryClient);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useCreatePermissionRequestMutation(
  options?: UseMutationOptions<PermissionRequest, Error, PermissionRequestCreate>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: (requests) => createPermissionRequest(requests),
    onSuccess: async (data, variables, onMutateResult, context) => {
      invalidatePermissionRequestsCache(queryClient);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useApprovePermissionRequestMutation(
  options?: UseMutationOptions<PermissionRequest, Error, { requestId: string; data: PermissionRequestApprove }>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: ({ requestId, data }) => approvePermissionRequest(requestId, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      invalidatePermissionRequestsCache(queryClient);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useDenyPermissionRequestMutation(
  options?: UseMutationOptions<PermissionRequest, Error, {requestId: string; data: PermissionRequestDeny }>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: ({requestId, data}) => denyPermissionRequest(requestId, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      invalidatePermissionRequestsCache(queryClient);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useCancelPermissionRequestMutation(
  options?: UseMutationOptions<PermissionRequest, Error, {requestId:string}>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: ({requestId}) => cancelPermissionRequest(requestId),
    onSuccess: async (data, variables, onMutateResult, context) => {
      invalidatePermissionRequestsCache(queryClient);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}